import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ThemeProvider } from "@/components/ThemeProvider";
import { SandboxProvider } from "@/contexts/SandboxContext";
import { CookieConsent } from "@/components/gdpr/CookieConsent";

import { ScrollToTopOnNavigate } from "@/components/common/ScrollToTopOnNavigate";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import Index from "./pages/Index";

// Redirect legacy ?community= URLs to /b/:slug
const IndexWithRedirect = () => {
  const [searchParams] = useSearchParams();
  const community = searchParams.get("community");
  if (community) {
    return <Navigate to={`/b/${community}`} replace />;
  }
  return <Index />;
};

const AppLoadingFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background px-6">
    <div className="flex flex-col items-center gap-4 text-center">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-primary" aria-hidden="true" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">Loading app</p>
        <p className="text-sm text-muted-foreground">Preparing your workspace…</p>
      </div>
    </div>
  </div>
);

// Lazy-loaded pages for code splitting
const Login = lazy(() => import("./pages/auth/Login"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const VerifyEmail = lazy(() => import("./pages/auth/VerifyEmail"));
const BusinessSignup = lazy(() => import("./pages/auth/BusinessSignup"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OAuthConsent = lazy(() => import("./pages/auth/OAuthConsent"));


// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const CommunitiesPage = lazy(() => import("./pages/admin/Communities"));
const NewCommunityPage = lazy(() => import("./pages/admin/NewCommunity"));
const CommunityDetailPage = lazy(() => import("./pages/admin/CommunityDetail"));
const EditCommunityPage = lazy(() => import("./pages/admin/EditCommunity"));
const AdminSettingsPage = lazy(() => import("./pages/admin/Settings"));
const AdminUsersPage = lazy(() => import("./pages/admin/Users"));
const CustomersPage = lazy(() => import("./pages/admin/Customers"));
const AdminCalendarPage = lazy(() => import("./pages/admin/Calendar"));

const AdminIntegrationsPage = lazy(() => import("./pages/admin/Integrations"));
const AdminReviewsPage = lazy(() => import("./pages/admin/Reviews"));
const PromotionsPage = lazy(() => import("./pages/admin/Promotions"));
const AdminTicketsPage = lazy(() => import("./pages/admin/Tickets"));
const AdminBrandingPage = lazy(() => import("./pages/admin/Branding"));
const AdminDataFactoryPage = lazy(() => import("./pages/admin/DataFactory"));
const AdminBillingPage = lazy(() => import("./pages/admin/Billing"));

// Staff pages
const StaffDashboard = lazy(() => import("./pages/staff/Dashboard"));
const StaffServices = lazy(() => import("./pages/staff/Services"));
const StaffSchedule = lazy(() => import("./pages/staff/Availability"));
const StaffProfile = lazy(() => import("./pages/staff/Profile"));
const StaffCustomersPage = lazy(() => import("./pages/staff/Customers"));
const StaffTeamPage = lazy(() => import("./pages/staff/Team"));
const StaffMessagesPage = lazy(() => import("./pages/staff/Messages"));


// Community pages
const CommunityProfile = lazy(() => import("./pages/community/CommunityProfile"));

// Business Admin pages
const ShopDashboard = lazy(() => import("./pages/shop/Dashboard"));
const ShopStaffPage = lazy(() => import("./pages/shop/Staff"));
const ShopSettingsPage = lazy(() => import("./pages/shop/Settings"));
const ShopCustomersPage = lazy(() => import("./pages/shop/Customers"));
const ShopReviewsPage = lazy(() => import("./pages/shop/Reviews"));
const ShopServicesPage = lazy(() => import("./pages/shop/Services"));
const ShopLeaveRequestsPage = lazy(() => import("./pages/shop/LeaveRequests"));
const ShopSchedulePage = lazy(() => import("./pages/shop/Schedule"));
const ShopAttendancePage = lazy(() => import("./pages/shop/Attendance"));
const ShopTimecardsPage = lazy(() => import("./pages/shop/Timecards"));
const HumanResourcesPage = lazy(() => import("./pages/shop/HumanResources"));
const ShopCardsPage = lazy(() => import("./pages/shop/Cards"));
const ShopPayslipsPage = lazy(() => import("./pages/shop/Payslips"));
const ShopCommissionsPage = lazy(() => import("./pages/shop/Commissions"));
const ShopCertificationsPage = lazy(() => import("./pages/shop/Certifications"));
const ShopShiftSwapsPage = lazy(() => import("./pages/shop/ShiftSwaps"));
const ShopOnboardingPage = lazy(() => import("./pages/shop/Onboarding"));
const ShopOvertimePage = lazy(() => import("./pages/shop/Overtime"));
const ShopMessagesPage = lazy(() => import("./pages/shop/Messages"));
// Business pages
const PricingPage = lazy(() => import("./pages/pricing/PricingPage"));
const SubscriptionPage = lazy(() => import("./pages/business/Subscription"));
const IntegrationsPage = lazy(() => import("./pages/business/Integrations"));
const CalendarPage = lazy(() => import("./pages/business/Calendar"));
const SupportPage = lazy(() => import("./pages/business/Support"));

// User pages
const UserProfile = lazy(() => import("./pages/user/Profile"));
const ProfileRoute = lazy(() => import("./pages/user/ProfileRoute"));
const UserSettings = lazy(() => import("./pages/user/Settings"));
const MobileAppPage = lazy(() => import("./pages/user/MobileApp"));

// Customer pages
const BookingsPage = lazy(() => import("./pages/customer/Bookings"));
const SavedProvidersPage = lazy(() => import("./pages/customer/SavedProviders"));
const BookNowPage = lazy(() => import("./pages/customer/BookNow"));
const ExplorePage = lazy(() => import("./pages/customer/ExplorePage"));

// Legal pages
const TermsPage = lazy(() => import("./pages/legal/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/legal/PrivacyPage"));
const CookiePolicyPage = lazy(() => import("./pages/legal/CookiePolicyPage"));
const LegalNoticePage = lazy(() => import("./pages/legal/LegalNoticePage"));

// Landing pages
const IndustriesPage = lazy(() => import("./pages/landing/IndustriesPage"));
const IndustryDetailPage = lazy(() => import("./pages/landing/IndustryDetailPage"));
const FeaturesPage = lazy(() => import("./pages/landing/FeaturesPage"));
const ContactPage = lazy(() => import("./pages/landing/ContactPage"));
const HelpCenterPage = lazy(() => import("./pages/landing/HelpCenterPage"));
const IntegrationArticlePage = lazy(() => import("./pages/landing/IntegrationArticlePage"));
const AIHRPage = lazy(() => import("./pages/landing/AIHRPage"));
const GreetingCardsLandingPage = lazy(() => import("./pages/landing/GreetingCardsPage"));
const GreetingCardsStartPage = lazy(() => import("./pages/landing/GreetingCardsStartPage"));
const GreetingCardsGalleryPage = lazy(() => import("./pages/landing/GreetingCardsGalleryPage"));
const QuickCardSignup = lazy(() => import("./pages/auth/QuickCardSignup"));
const BlogPage = lazy(() => import("./pages/landing/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/landing/BlogPostPage"));
const SitemapPage = lazy(() => import("./pages/landing/SitemapPage"));
const StatusPage = lazy(() => import("./pages/landing/StatusPage"));
const SignCardPage = lazy(() => import("./pages/public/SignCard"));
const ApplyJobPage = lazy(() => import("./pages/public/ApplyJob"));
const DownloadCardPage = lazy(() => import("./pages/public/DownloadCard"));
const PickInterviewSlotPage = lazy(() => import("./pages/public/PickInterviewSlot"));
const UnsubscribePage = lazy(() => import("./pages/public/Unsubscribe"));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Subdomain detection: redirect slug.businesspal.tech -> /b/slug (future-proofing)
const SubdomainRedirect = () => {
  const hostname = window.location.hostname;
  const baseDomain = "businesspal.tech";
  if (hostname.endsWith(`.${baseDomain}`) && hostname !== baseDomain && hostname !== `www.${baseDomain}`) {
    const slug = hostname.replace(`.${baseDomain}`, "");
    return <Navigate to={`/b/${slug}`} replace />;
  }
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <TooltipProvider>
        <SandboxProvider>
          <AuthProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ErrorBoundary>
                <Suspense fallback={<AppLoadingFallback />}>
                  <SubdomainRedirect />
                  <Routes>
                    <Route path="/" element={<IndexWithRedirect />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/signup/business" element={<BusinessSignup />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />



                    {/* Public routes */}
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/industries" element={<IndustriesPage />} />
                    <Route path="/industries/:industry" element={<IndustryDetailPage />} />
                    <Route path="/features" element={<FeaturesPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/ai-hr" element={<AIHRPage />} />
                    <Route path="/greeting-cards" element={<GreetingCardsLandingPage />} />
                    <Route path="/greeting-cards/start" element={<GreetingCardsStartPage />} />
                    <Route path="/greeting-cards/gallery" element={<GreetingCardsGalleryPage />} />
                    <Route path="/greeting-cards/quick-start" element={<QuickCardSignup />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/sitemap" element={<SitemapPage />} />
                    <Route path="/status" element={<StatusPage />} />

                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/tickets"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <AdminTicketsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/communities"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <CommunitiesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/communities/new"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <NewCommunityPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/communities/:id"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <CommunityDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/communities/:id/edit"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <EditCommunityPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <AdminSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <AdminUsersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/customers"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <CustomersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/calendar"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <AdminCalendarPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/promotions"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <PromotionsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/integrations"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <AdminIntegrationsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/reviews"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <AdminReviewsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/branding"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <AdminBrandingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/data-factory"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <AdminDataFactoryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/billing"
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <AdminBillingPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Business Admin Routes */}
                    <Route
                      path="/business"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/staff"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopStaffPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/settings"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/customers"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopCustomersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/reviews"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopReviewsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/services"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopServicesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/leave-requests"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopLeaveRequestsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/subscription"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <SubscriptionPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/integrations"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <IntegrationsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/calendar"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <CalendarPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/support"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <SupportPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/hr"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <HumanResourcesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/schedule"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopSchedulePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/attendance"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopAttendancePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/timecards"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopTimecardsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/messages"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopMessagesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/cards"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopCardsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/payslips"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopPayslipsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/commissions"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopCommissionsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/certifications"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopCertificationsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/shift-swaps"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopShiftSwapsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/onboarding"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopOnboardingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business/overtime"
                      element={
                        <ProtectedRoute requiredRole="business_admin">
                          <ShopOvertimePage />
                        </ProtectedRoute>
                      }
                    />


                    {/* Staff Routes */}
                    <Route
                      path="/staff"
                      element={
                        <ProtectedRoute requiredRole="staff">
                          <StaffDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/staff/services"
                      element={
                        <ProtectedRoute requiredRole="staff">
                          <StaffServices />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/staff/schedule"
                      element={
                        <ProtectedRoute requiredRole="staff">
                          <StaffSchedule />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/staff/availability"
                      element={
                        <ProtectedRoute requiredRole="staff">
                          <StaffSchedule />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/staff/profile"
                      element={
                        <ProtectedRoute>
                          <StaffProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/staff/customers"
                      element={
                        <ProtectedRoute requiredRole="staff">
                          <StaffCustomersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/staff/team"
                      element={
                        <ProtectedRoute requiredRole="staff">
                          <StaffTeamPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/staff/messages"
                      element={
                        <ProtectedRoute requiredRole="staff">
                          <StaffMessagesPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* User Routes */}
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <ProfileRoute />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <UserSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/mobile-app"
                      element={
                        <ProtectedRoute>
                          <MobileAppPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Customer Routes */}
                    <Route
                      path="/bookings"
                      element={
                        <ProtectedRoute>
                          <BookingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/book"
                      element={
                        <ProtectedRoute>
                          <BookNowPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/book/:slug" element={<BookNowPage />} />
                    <Route
                      path="/favorites"
                      element={
                        <ProtectedRoute>
                          <SavedProvidersPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Public Community Profile */}
                    <Route path="/b/:slug" element={<CommunityProfile />} />

                    {/* Public Explore Page */}
                    <Route path="/explore" element={<ExplorePage />} />

                    {/* Help Center */}
                    <Route path="/help" element={<HelpCenterPage />} />
                    <Route path="/help/integrations/:provider" element={<IntegrationArticlePage />} />

                    {/* Public Card Signing */}
                    <Route path="/cards/sign/:token" element={<SignCardPage />} />
                    <Route path="/cards/:cardId/download" element={<DownloadCardPage />} />

                    {/* Public Job Application */}
                    <Route path="/jobs/apply/:token" element={<ApplyJobPage />} />

                    {/* Public Interview Slot Picker */}
                    <Route path="/interview/:token" element={<PickInterviewSlotPage />} />

                    {/* Public Unsubscribe */}
                    <Route path="/unsubscribe" element={<UnsubscribePage />} />

                    {/* Legal Pages */}
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                    <Route path="/legal-notice" element={<LegalNoticePage />} />

                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <ScrollToTopOnNavigate />
                <CookieConsent />
                
              </ErrorBoundary>
            </BrowserRouter>
          </AuthProvider>
        </SandboxProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
